// FieldModal.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useFieldModalState from './hooks/useFieldModalState';

// CSS
import './FieldModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import DefaultValueField from './components/DefaultValueField';
import EnumOptionsField from './components/EnumOptionsField';
import FieldDeleteConfirm from './components/FieldDeleteConfirm';
import FieldDescriptionField from './components/FieldDescriptionField';
import FieldLabelField from './components/FieldLabelField';
import FieldTargetTypeField from './components/FieldTargetTypeField';
import FieldTypeField from './components/FieldTypeField';

/**
 * FieldModal
 *
 * Modal for adding or editing a schema form field.
 * 
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onSave - Callback when the field is saved.
 * @param {string[]|null} allowedFieldTypes - Optional allowlist of field types to expose in the picker.
 * 
 * @returns {ReactNode} Rendered FieldModal component.
 */
const FieldModal = ({
    isOpen,
    setOpen,
    onSave,
    onDelete,
    isSaving = false,
    editingField = null,
    nodeTypeLabel = '',
    existingFields = [],
    workspaceKey = null,
    allowedFieldTypes = null,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    const {
        modalState,
        formState,
        enumOptionsState,
    } = useFieldModalState({
        isOpen,
        onSave,
        editingField,
        nodeTypeLabel,
        existingFields,
        allowedFieldTypes,
    });

    return (
        <>
            <Modal
                title={modalState.modalTitle}
                isOpen={isOpen}
                onClose={() => setOpen(false)}
                width={isMobile ? "100%" : 480}
                height="auto"
            >
                <ModalForm>

                    {/* TYPE */}
                    <FieldTypeField
                        draftType={formState.draft.type}
                        mergedTypeOptions={formState.mergedTypeOptions}
                        theme={theme}
                        error={formState.errors.type}
                        onTypeChange={(type) => {
                            formState.clearError('type');
                            formState.patchDraft({ type });
                        }}
                        disabled={modalState.isEditing}
                    />

                    {/* LABEL */}
                    <FieldLabelField
                        value={formState.draft.label}
                        theme={theme}
                        error={formState.errors.label}
                        onChange={(label) => {
                            formState.clearError('label');
                            formState.patchDraft({ label });
                        }}
                    />

                    {/* DESCRIPTION */}
                    <FieldDescriptionField
                        value={formState.draft.fieldDescription}
                        theme={theme}
                        error={formState.errors.fieldDescription}
                        onChange={(fieldDescription) => formState.patchDraft({ fieldDescription })}
                    />

                    {/* TARGET TYPE FOR NODE LIST */}
                    {formState.isNodeListType && (
                        <FieldTargetTypeField
                            draftTargetType={formState.draft.targetType}
                            targetTypeOptions={formState.targetTypeOptions}
                            theme={theme}
                            error={formState.errors.targetType}
                            disabled={modalState.isEditing}
                            onTargetTypeChange={(targetType) => {
                                formState.clearError('targetType');
                                formState.patchDraft({ targetType });
                            }}
                        />
                    )}

                    {/* OPTIONS */}
                    {formState.isOptionsType && (
                        <>
                            <EnumOptionsField
                                addLabel={formState.isMeasurementType ? 'Add Unit' : 'Add Option'}
                                emptyCopy={formState.isMeasurementType ? 'No units yet.' : 'No options yet.'}
                                label={formState.isMeasurementType ? 'Units' : 'Options'}
                                placeholder={formState.isMeasurementType ? 'e.g. ft' : 'Internal value'}
                                selectedEnumOptions={enumOptionsState.selectedEnumOptions}
                                optionValueDrafts={enumOptionsState.optionValueDrafts}
                                setOptionValueDrafts={enumOptionsState.setOptionValueDrafts}
                                onOptionValueCommit={enumOptionsState.handleOptionValueCommit}
                                onOptionValueKeyDown={enumOptionsState.handleOptionValueKeyDown}
                                onOptionColorChange={enumOptionsState.handleOptionColorChange}
                                onOptionIconChange={enumOptionsState.handleOptionIconChange}
                                onOptionMove={enumOptionsState.handleOptionMove}
                                onOptionRemove={enumOptionsState.handleOptionRemove}
                                onOptionAdd={enumOptionsState.handleOptionAdd}
                                showColors={!formState.isMeasurementType}
                                showIcons={!formState.isMeasurementType}
                                theme={theme}
                                optionsError={formState.errors.options}
                            />
                        </>
                    )}

                    {formState.isMeasurementType ? (

                        <ModalField>

                            <ModalLabel label="Default Unit" />
                            <ModalHint hint="Select the default unit for the field." />

                            <SelectInput
                                value={formState.draft.defaultUnit || ''}
                                error={!!formState.errors.defaultUnit}
                                helperText={formState.errors.defaultUnit}
                                disabled={enumOptionsState.selectedEnumOptions.length === 0}
                                onChange={(event) => {
                                    formState.clearError('defaultUnit');
                                    formState.patchDraft({ defaultUnit: event.target.value });
                                }}
                            >
                                {enumOptionsState.selectedEnumOptions.map((option) => (
                                    <option key={option.id || option.value} value={option.value}>
                                        {option.value}
                                    </option>
                                ))}
                            </SelectInput>

                        </ModalField>

                    ) : null}

                    {!formState.isDefaultUnsupported && (
                        <DefaultValueField
                            draft={formState.draft}
                            isEnumType={formState.isEnumType}
                            isTagsType={formState.isTagsType}
                            selectedEnumOptions={enumOptionsState.selectedEnumOptions}
                            theme={theme}
                            onDefaultChange={(nextDefault) => formState.patchDraft({ default: nextDefault })}
                        />
                    )}

                    {formState.draft.type === 'percentage' && (

                        <ModalField>

                            <ModalLabel label="Snap To (%)" />
                            <ModalHint hint="The increment value for snapping percentages." />

                            <TextInput
                                type="number"
                                value={formState.draft.snapTo ?? 5}
                                min={1}
                                step={1}
                                placeholder="5"
                                onChange={(event) => {
                                    const nextValue = event.target.value;
                                    formState.patchDraft({ snapTo: nextValue });
                                }}
                            />

                            {formState.errors.snapTo ? <div className="field-modal-error">{formState.errors.snapTo}</div> : null}

                        </ModalField>

                    )}

                </ModalForm>

                <ModalButtons
                    style={{
                        justifyContent: 'space-between',
                    }}>

                    <div className="field-modal-buttons-left">
                        {modalState.isEditing && typeof onDelete === 'function' && (
                            <Button
                                iconKey="delete"
                                onClick={() => modalState.setConfirmDeleteOpen(true)}
                                label="Delete"
                                enabled={modalState.canDelete && !isSaving}
                            />
                        )}
                    </div>

                    <div className="field-modal-buttons-right">
                        <Button
                            iconKey="close"
                            onClick={() => setOpen(false)} label="Cancel"
                            enabled={!isSaving}
                        />

                        <Button
                            iconKey="check"
                            onClick={formState.handleSave} label={isSaving ? 'Saving...' : 'Save'}
                            enabled={!isSaving}
                        />
                    </div>

                </ModalButtons>

            </Modal >

            <FieldDeleteConfirm
                isOpen={modalState.confirmDeleteOpen}
                setOpen={modalState.setConfirmDeleteOpen}
                deleteLabel={modalState.deleteLabel}
                canDelete={modalState.canDelete}
                onDelete={onDelete}
            />
        </>
    );
};


export default FieldModal;
